/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React, { useEffect, useMemo } from 'react';
import { GitBranch, AlertTriangle } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { useToast } from '../hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import { ActiveProductIndicator } from '../components/common/ActiveProductIndicator';
import { ConflictResolutionDialog } from '../components/conflicts';
import { SkillShopService } from '../services/skillShopService';
import { getOrganizationSettings } from '@shared/lib/api/organization_settings';
import type { RepositorySettings, VelocitySyncSettings, VelocitySettings } from '@shared/types/organizationSettings';
import {
  WorktreeSidebar,
  WorktreeStatusInfo,
  SyncCollapsibleSection,
  FileChangesList,
  CommitHistoryPanel,
  DiffViewerPanel,
  CommitDialog,
  CreatePRDialog,
  RevertDialog,
  RevertCommitFileConflictDialog,
  VelocityPRDialog,
  generateUniquePRTitle,
  DeleteWorktreeDialog,
} from '../components/changes';
import {
  useChangesSettings,
  useWorktreeManagement,
  useSyncOperations,
  useGitOperations,
  useSyncModes,
} from '../hooks/changes';

export function Changes() {
  const { activeProduct, githubConfig, isGitHubConfigured, gitlabConfig, isGitLabConfigured, sourceControlProvider } = useApp();
  const { toast } = useToast();

  const skillShopService = useMemo(() => new SkillShopService(), []);

  const isSourceControlConfigured = sourceControlProvider === 'github' ? isGitHubConfigured : isGitLabConfigured;
  const accessToken = sourceControlProvider === 'github' ? githubConfig?.accessToken : gitlabConfig?.accessToken;

  const { data: orgSettings } = useQuery(getOrganizationSettings());
  const repoSettings = orgSettings?.repositorySettings as RepositorySettings | undefined;
  const velocityOrgSettings = orgSettings?.velocitySettings as VelocitySettings | undefined;
  const defaultBranch = repoSettings?.defaultBranch || 'main';
  const syncMode = velocityOrgSettings?.sync?.syncMode || 'velocity-mode';
  const orgSimpleMode = velocityOrgSettings?.simpleMode ?? true;

  // --- Hook: Settings & UI layout ---
  const settings = useChangesSettings(skillShopService);

  // --- Hook: Worktree management ---
  const worktree = useWorktreeManagement(activeProduct, sourceControlProvider, toast);

  // --- Hook: Sync operations (conflicts, remote velocity, PRs, sync-to-main) ---
  const sync = useSyncOperations({
    activeProduct,
    sourceControlProvider,
    accessToken,
    velocitySettings: settings.velocitySettings,
    toast,
    loadStatus: worktree.loadStatus,
    loadCommitHistory: worktree.loadCommitHistory,
    gitlabConfig,
    defaultBranch,
    organizationId: orgSettings?.organizationId,
  });

  // --- Hook: Git operations (commit, push, PR, revert, diff, file selection) ---
  const git = useGitOperations({
    activeProduct,
    selectedWorktree: worktree.selectedWorktree,
    status: worktree.status,
    accessToken,
    sourceControlProvider,
    gitlabConfig,
    defaultBranch,
    toast,
    loadStatus: worktree.loadStatus,
    loadCommitHistory: worktree.loadCommitHistory,
    loadSyncToMainAnalysis: sync.loadSyncToMainAnalysis,
    skillShopService,
  });

  // --- Hook: Sync modes (simple mode, velocity mode) ---
  const syncModes = useSyncModes({
    activeProduct,
    selectedWorktree: worktree.selectedWorktree,
    status: worktree.status,
    selectedFiles: git.selectedFiles,
    skillShopService,
    toast,
    loadStatus: worktree.loadStatus,
    loadCommitHistory: worktree.loadCommitHistory,
    loadSyncToMainAnalysis: sync.loadSyncToMainAnalysis,
    loadRemoteVelocityStatus: sync.loadRemoteVelocityStatus,
    handleSync: sync.handleSync,
    handleSyncToMain: sync.handleSyncToMain,
    accessToken,
    sourceControlProvider,
    gitlabConfig,
    setCommitting: git.setCommitting,
    setSyncing: sync.setSyncing,
  });

  // --- Effects ---

  // Load worktrees when activeProduct repository is available
  useEffect(() => {
    if (activeProduct?.repositoryUrl) {
      worktree.loadWorktrees();
    }
  }, [activeProduct?.repositoryUrl, worktree.loadWorktrees]);

  // Load status and history when a worktree is selected
  useEffect(() => {
    if (worktree.selectedWorktree && activeProduct?.repositoryUrl) {
      worktree.loadStatus();
      worktree.loadCommitHistory();
      sync.loadRemoteVelocityStatus();
    }
  }, [worktree.selectedWorktree, activeProduct?.repositoryUrl, worktree.loadStatus, worktree.loadCommitHistory, sync.loadRemoteVelocityStatus]);

  // Load sync-to-main analysis when worktree and repository are available
  useEffect(() => {
    if (worktree.selectedWorktree && activeProduct?.repositoryUrl) {
      sync.loadSyncToMainAnalysis();
    }
  }, [worktree.selectedWorktree, activeProduct?.repositoryUrl, sync.loadSyncToMainAnalysis]);

  // Auto-select all files and preview first file in simple mode (on initial load)
  useEffect(() => {
    if (!settings.velocitySettings.changesSimpleMode) return;
    if (!worktree.status?.files?.length || !worktree.selectedWorktree) return;

    const worktreeKey = worktree.selectedWorktree.path;
    if (settings.hasAutoSelectedRef.current === worktreeKey) return;

    settings.hasAutoSelectedRef.current = worktreeKey;
    git.setSelectedFiles(new Set(worktree.status.files.map(f => f.path)));

    if (worktree.status.files.length > 0) {
      git.loadDiff(worktree.status.files[0].path);
    }
  }, [worktree.status, worktree.selectedWorktree, settings.velocitySettings.changesSimpleMode, git.loadDiff]);

  // Reset selections and auto-selection ref when switching worktrees
  useEffect(() => {
    if (worktree.selectedWorktree) {
      if (settings.hasAutoSelectedRef.current && settings.hasAutoSelectedRef.current !== worktree.selectedWorktree.path) {
        settings.hasAutoSelectedRef.current = null;
        git.clearSelectionsForWorktreeSwitch();
      }
    }
  }, [worktree.selectedWorktree]);

  // --- Render ---

  if (!activeProduct?.repositoryUrl) {
    return (
      <div className="flex h-full bg-white changes-page-container">
        <div className="w-64 bg-white changes-page-sidebar">
          <div className="px-4 py-4 changes-page-header">
            <h2 className="text-lg font-semibold text-gray-900">Worktrees</h2>
          </div>
          <div className="flex items-center justify-center h-32 text-gray-500 text-sm px-4 text-center">
            No product selected
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <AlertTriangle size={48} className="mx-auto text-yellow-500 mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Repository Selected</h2>
            <p className="text-gray-600">Please select a product with a repository to view changes</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-white changes-page-container">
      {/* Left Sidebar - Worktree List */}
      <WorktreeSidebar
        worktrees={worktree.worktrees}
        selectedWorktree={worktree.selectedWorktree}
        onSelectWorktree={worktree.setSelectedWorktree}
        onRefresh={worktree.loadWorktrees}
        loading={worktree.loading}
        refreshing={worktree.refreshing}
        repositoryUrl={activeProduct?.repositoryUrl}
        width={settings.sidebarWidth}
        onWidthChange={settings.handleSidebarWidthChange}
        isCollapsed={settings.sidebarCollapsed}
        onCollapsedChange={settings.handleSidebarCollapsedChange}
        onDeleteWorktree={worktree.handleDeleteWorktreeRequest}
      />

      {/* Main Content Area */}
      {worktree.selectedWorktree ? (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top Header */}
          <div className="bg-white px-6 py-4 changes-page-header">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Changes</h1>
                  <p className="text-sm text-gray-600 mt-1">
                    Viewing changes in {worktree.selectedWorktree.path.split('/').pop()}
                  </p>
                </div>
                <div className="h-8 w-px bg-gray-200" />
                <ActiveProductIndicator />
              </div>
            </div>
          </div>

          {/* Changes Panel */}
          <div className="flex-1 flex overflow-hidden">
            {/* Left Panel - File List */}
            <div
              ref={settings.changesPanelRef}
              className="bg-white flex flex-col changes-page-panel relative flex-shrink-0"
              style={{ width: settings.changesPanelWidth }}
            >
              <WorktreeStatusInfo status={worktree.status} />

              <SyncCollapsibleSection
                onSync={sync.handleSync}
                syncing={sync.syncing}
                onCheckConflicts={sync.checkForConflicts}
                checkingConflicts={sync.checkingConflicts}
                hasConflicts={sync.hasConflicts}
                conflicts={sync.conflicts}
                onShowConflictDialog={() => sync.setShowConflictDialog(true)}
                showTeamCollaboration={false}
                remoteVelocityStatus={sync.remoteVelocityStatus}
                loadingRemoteStatus={sync.loadingRemoteStatus}
                onPushToTeam={sync.handlePushToTeam}
                pushingToTeam={sync.pushingToTeam}
                onPullFromTeam={sync.handlePullFromTeam}
                pullingFromTeam={sync.pullingFromTeam}
                onShowVelocityPRDialog={() => {
                  if (!sync.velocityPRTitle) {
                    sync.setVelocityPRTitle(generateUniquePRTitle(activeProduct?.title, defaultBranch));
                  }
                  sync.setVelocityPRError(null);
                  sync.setShowVelocityPRDialog(true);
                }}
                syncToMainAnalysis={sync.syncToMainAnalysis}
                syncToMainDecision={sync.syncToMainDecision}
                loadingSyncToMainAnalysis={sync.loadingSyncToMainAnalysis}
                onSyncToMain={sync.handleSyncToMain}
                syncingToMain={sync.syncingToMain}
                onRefreshAnalysis={sync.loadSyncToMainAnalysis}
                isSourceControlConfigured={isSourceControlConfigured}
                sourceControlProvider={sourceControlProvider}
                defaultBranch={defaultBranch}
                simpleMode={settings.velocitySettings.changesSimpleMode ?? true}
                onSimpleModeSync={syncModes.handleSimpleModeSync}
                committing={git.committing}
                uncommittedFilesCount={worktree.status?.files?.length || 0}
                syncMode={syncMode}
                onVelocityModeSync={syncModes.handleVelocityModeSync}
                orgSimpleMode={orgSimpleMode}
                onShowCommitDialog={git.handleShowCommitDialog}
              />

              <FileChangesList
                status={worktree.status}
                selectedFiles={git.selectedFiles}
                selectedFile={git.selectedFile}
                loading={worktree.loading}
                refreshing={worktree.refreshing}
                onToggleFileSelection={git.toggleFileSelection}
                onSelectAllFiles={git.selectAllFiles}
                onClearSelection={git.clearSelection}
                onFileClick={git.loadDiff}
                onShowCommitDialog={git.handleShowCommitDialog}
                onShowRevertDialog={(all) => {
                  git.setRevertAll(all);
                  git.setShowRevertDialog(true);
                }}
                onShowPRDialog={() => git.setShowPRDialog(true)}
                isSourceControlConfigured={isSourceControlConfigured}
                sourceControlProvider={sourceControlProvider}
              />

              <CommitHistoryPanel
                commitHistory={worktree.commitHistory}
                loadingHistory={worktree.loadingHistory}
                historyExpanded={worktree.historyExpanded}
                onToggleExpanded={() => worktree.setHistoryExpanded(!worktree.historyExpanded)}
                onPush={git.handlePush}
                pushing={git.pushing}
                isSourceControlConfigured={isSourceControlConfigured}
                sourceControlProvider={sourceControlProvider}
                defaultBranch={defaultBranch}
                selectedCommitHash={git.selectedCommitHash}
                selectedCommitFiles={git.selectedCommitFiles}
                loadingCommitFiles={git.loadingCommitFiles}
                onCommitClick={git.loadCommitFilesForHash}
                onCommitFileClick={git.loadCommitFileDiff}
                onCloseCommitFiles={git.clearCommitFilesView}
              />

              {/* Resize Handle */}
              <div
                className="changes-panel-resize-handle"
                onMouseDown={settings.handleChangesPanelMouseDown}
              >
                <div className="changes-panel-resize-grip">
                  <div className="w-0.5 h-4 bg-gray-400 rounded-full" />
                </div>
              </div>
            </div>

            {/* Right Panel - Diff Viewer */}
            <div className="flex-1 bg-gray-50 flex flex-col min-w-0">
              <DiffViewerPanel
                selectedFile={git.selectedFile}
                diff={git.diff}
                fileStatus={git.selectedFileStatus}
                isNewFile={git.isNewFile}
                isDirectory={git.isDirectory}
                directoryFiles={git.directoryFiles}
                loadingDiff={git.loadingDiff}
                onClose={git.clearDiffView}
                onFileClick={git.loadDiff}
                commitHash={git.selectedCommitHash}
                onRevertCommitFile={git.handleRevertCommitFile}
                revertingCommitFile={git.revertingCommitFile}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <GitBranch size={48} className="mx-auto text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">No Worktree Selected</h2>
            <p className="text-gray-600">
              {worktree.worktrees.length > 0
                ? 'Select a worktree from the sidebar to view changes'
                : 'No worktrees available for this product'}
            </p>
          </div>
        </div>
      )}

      {/* Dialogs */}
      <CommitDialog
        isOpen={git.showCommitDialog}
        onOpenChange={git.setShowCommitDialog}
        commitMessage={git.commitMessage}
        onCommitMessageChange={git.setCommitMessage}
        selectedFilesCount={git.selectedFiles.size}
        onCommit={git.handleCommit}
        committing={git.committing}
        generatingMessage={git.generatingCommitMessage}
      />

      <CreatePRDialog
        isOpen={git.showPRDialog}
        onOpenChange={git.setShowPRDialog}
        prTitle={git.prTitle}
        onPrTitleChange={git.setPrTitle}
        prBody={git.prBody}
        onPrBodyChange={git.setPrBody}
        prBaseBranch={git.prBaseBranch}
        onPrBaseBranchChange={git.setPrBaseBranch}
        onCreatePR={git.handleCreatePR}
        creatingPR={git.creatingPR}
        sourceControlProvider={sourceControlProvider}
      />

      <RevertDialog
        isOpen={git.showRevertDialog}
        onOpenChange={git.setShowRevertDialog}
        revertAll={git.revertAll}
        selectedFilesCount={git.selectedFiles.size}
        onRevert={git.handleRevert}
        reverting={git.reverting}
      />

      <VelocityPRDialog
        isOpen={sync.showVelocityPRDialog}
        onOpenChange={(open) => {
          sync.setShowVelocityPRDialog(open);
          if (!open) {
            sync.setVelocityPRError(null);
          }
        }}
        prTitle={sync.velocityPRTitle}
        onPrTitleChange={sync.setVelocityPRTitle}
        prBody={sync.velocityPRBody}
        onPrBodyChange={sync.setVelocityPRBody}
        onCreatePR={sync.handleCreateVelocityPR}
        creatingPR={sync.creatingVelocityPR}
        sourceControlProvider={sourceControlProvider}
        defaultBranch={defaultBranch}
        error={sync.velocityPRError}
        onClearError={() => sync.setVelocityPRError(null)}
      />

      <DeleteWorktreeDialog
        isOpen={worktree.showDeleteWorktreeDialog}
        onOpenChange={worktree.setShowDeleteWorktreeDialog}
        worktreeName={worktree.worktreeToDelete?.path.split('/').pop() || ''}
        hasUnmergedChanges={worktree.deleteWorktreeStatus.hasUnmergedChanges}
        uncommittedFilesCount={worktree.deleteWorktreeStatus.uncommittedFilesCount}
        unpushedCommitsCount={worktree.deleteWorktreeStatus.unpushedCommitsCount}
        onConfirm={worktree.handleDeleteWorktreeConfirm}
        deleting={worktree.deletingWorktree}
      />

      <ConflictResolutionDialog
        isOpen={sync.showConflictDialog}
        onClose={() => sync.setShowConflictDialog(false)}
        conflicts={sync.conflicts}
        repositoryUrl={activeProduct?.repositoryUrl || ''}
        sourceControlProvider={sourceControlProvider}
        onAllResolved={sync.handleConflictsResolved}
        autoApplyThreshold={0.8}
      />

      <RevertCommitFileConflictDialog
        isOpen={git.showCommitFileRevertConflictModal}
        onOpenChange={git.setShowCommitFileRevertConflictModal}
        conflict={git.commitFileRevertConflict}
      />
    </div>
  );
}
